import React, { useCallback, useMemo } from 'react';

import { Paper } from '@mantine/core';
import { KioskEventList } from 'types/generated';
import { getFormattedCoachesNames } from 'utils/event.helpers';
import { formatDatesForEvent, getStartsIn } from 'utils/formatters';

import EventCardAppointment from './EventCardAppointment';
import EventCardClass from './EventCardClass';

interface EventCardProps {
  event: KioskEventList;
  onClick: (value: string) => void;
  eventType: string;
  isDesktop?: boolean;
}

type StartsInStatus = 'ongoing' | 'upcoming' | 'ended';
type StartsInStatusesColors = {
  text: string;
  background: string;
  border: string;
  borderWidth: string;
};

const EventCard = ({ event, onClick, eventType, isDesktop }: EventCardProps): JSX.Element => {
  const getFormattedAthletesForAppointments = (): string => {
    if (event.eventType === 'kilo_class_type') return '';
    const athletesLimit = isDesktop ? 3 : 2;

    const athletesForAppointments = event.athleteAppointments.map(aap => aap.athlete);

    if (athletesForAppointments.length === 0) return '';

    const formattedString = athletesForAppointments.reduce((acc, athlete, index) => {
      if (index >= athletesLimit) return acc;

      const firstNameCap = athlete.firstName.charAt(0).toUpperCase() + athlete.firstName.slice(1);
      const lastNameInitial = athlete.lastName.charAt(0).toUpperCase() + athlete.lastName.slice(1);
      const name = `${firstNameCap} ${lastNameInitial}`;

      return acc ? `${acc}, ${name}` : name;
    }, '');

    const remaining =
      athletesForAppointments.length > athletesLimit
        ? `, +${athletesForAppointments.length - athletesLimit}`
        : '';
    return `${formattedString}${remaining}`;
  };

  const startsInStatus = useCallback((): StartsInStatus => {
    const startsIn = getStartsIn(event);
    if (startsIn === 'Ongoing') {
      return 'ongoing';
    } else if (startsIn === 'Ended') {
      return 'ended';
    } else {
      return 'upcoming';
    }
  }, [event]);

  const returnStylesForStartsInStatuses = useMemo((): StartsInStatusesColors => {
    const status = startsInStatus();
    switch (status) {
      case 'ongoing':
        return {
          text: '#61CC75',
          background: '#E2F6E6',
          border: '#61CC75',
          borderWidth: '3px',
        };
      case 'upcoming':
        return {
          text: '#15B8FF',
          background: '#E1F3FE',
          border: 'rgb(222, 226, 230)',
          borderWidth: '0px',
        };

      case 'ended':
        return {
          text: 'black',
          background: 'transparent',
          border: 'rgb(222, 226, 230)',
          borderWidth: '0px',
        };

      default:
        return {
          text: 'black',
          background: 'transparent',
          border: 'rgb(222, 226, 230)',
          borderWidth: '0px',
        };
    }
  }, [startsInStatus]);

  const getClassAttendance = useMemo((): string => {
    const attendance = event.athleteKiloClasses.length;

    return attendance === event.capacity ? 'Full' : `${event.athleteKiloClasses.length} / ${event.capacity}`;
  }, [event]);

  const getAppointmentAthletesProfilePictures = useMemo((): string => {
    if (event.athleteAppointments.length) {
      return event.athleteAppointments[0].athlete.profilePicture || '';
    }
    return '';
  }, [event.athleteAppointments]);

  return (
    <Paper
      shadow='md'
      p={isDesktop ? 16 : 10}
      style={{
        borderWidth: returnStylesForStartsInStatuses?.borderWidth,
        borderStyle: 'solid',
        borderColor: returnStylesForStartsInStatuses?.border,
        maxWidth: '100%',
      }}
    >
      {eventType === 'class' ? (
        <EventCardClass
          classAttendance={getClassAttendance}
          coachNames={getFormattedCoachesNames(event, 1)}
          duration={event.duration || 0}
          eventDurationDateAndTime={formatDatesForEvent(event, isDesktop)}
          isEnded={startsInStatus() !== 'ended'}
          location={event.location || '-'}
          onClick={() => onClick(event.eventId)}
          returnStylesForStartsInStatuses={returnStylesForStartsInStatuses}
          startsIn={getStartsIn(event)}
          title={event.eventName}
          workoutName={event.workoutName}
          key={event.eventId}
          isDesktop={isDesktop}
        />
      ) : (
        <EventCardAppointment
          coachNames={getFormattedCoachesNames(event, 1)}
          duration={event.duration || 0}
          eventDurationDateAndTime={formatDatesForEvent(event, isDesktop)}
          isEnded={startsInStatus() !== 'ended'}
          location={event.location || '-'}
          onClick={() => onClick(event.eventId)}
          returnStylesForStartsInStatuses={returnStylesForStartsInStatuses}
          startsIn={getStartsIn(event)}
          title={getFormattedAthletesForAppointments()}
          key={event.eventId}
          eventName={event.eventName}
          profilePicture={getAppointmentAthletesProfilePictures}
          isDesktop={isDesktop}
        />
      )}
    </Paper>
  );
};

export default EventCard;
